import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Sidebar from '../Sidebar';
import AppHeader from '../AppHeader';
import ConceptsBrowser from './ConceptsBrowser';
import PoliciesBrowser from './PoliciesBrowser';
import ParametersBrowser from './ParametersBrowser';
import CoverageBrowser from './CoverageBrowser';
import DocumentsBrowser from './DocumentsBrowser';
import CoverageBanner from './CoverageBanner';
import OntologyPreviewDrawer from './OntologyPreviewDrawer';
import ChangeSetsBrowser from './changeSets/ChangeSetsBrowser';
import ScenarioVocabularyBrowser from './ScenarioVocabularyBrowser';
import ChangeSetReviewDrawer from './changeSets/ChangeSetReviewDrawer';
import IngestDocumentDialog from './changeSets/IngestDocumentDialog';
import { useOntologyFilters } from './OntologyBrowser';
import {
    CHANGE_SETS_KIND, COVERAGE_KIND, DOC_KIND, KIND_LABEL, OntologyKind, VOCABULARY_KIND, codeOf, isLinkableCode,
    safeDecodeCode,
} from './ontologyFormat';
import { KINDS, isKind } from '../../api/ontologyApi';
import { dsVar } from '../../theme/tokens';

/**
 * The ontology browse area: the risk library, the policies that govern it, and the
 * thresholds those policies set.
 *
 * These entity types are a MESH, not a hierarchy — a policy governs many terms and a term
 * is governed by many policies — so this is three peer tabs rather than a tree. Analysts
 * arrive from several directions (policy-first, term-first, threshold-first, or from an
 * activity node in the agents catalog), and no one of those is privileged with the
 * navigation. What DOES get privileged is the question, which is why /ontology itself is
 * now a landing page and this screen is what you reach after choosing one.
 *
 * Row clicks open a preview drawer rather than navigating (see OntologyPreviewDrawer). The
 * drawer lives here, not inside OntologyBrowser, because docking it requires it to be a
 * flex child of this page's row container.
 */
/**
 * TWO TABS THAT ARE NOT `kind`s, for two different reasons.
 *
 * COVERAGE is a view over the collections rather than a resource: no API path, no code
 * field, no detail route.
 *
 * DOCUMENTS is a real resource with both — but a READ-ONLY one. It has no create, approve
 * or supersede endpoint, and OntologyDetailPage decides which lifecycle buttons to render
 * from apiForKind(kind) plus lifecycleStateOf(document). Adding it to KINDS would
 * therefore grow an Approve button that POSTs to a route the control plane does not serve.
 *
 * Keeping both out of KINDS is what stops either leaking into apiForKind, codeOf and the
 * preview drawer.
 */
// Shared with coverageFindingHref, so the tab and the links into it cannot drift apart.
const COVERAGE_TAB = COVERAGE_KIND;
// Documents before Coverage: a resource, then the report over the resources. Policy Updates
// sit beside Documents — they are documents on their way into the library.
// Your vocabulary sits with the library it translates into: the institution's own terms
// (its rule codes), mapped onto the typologies the Risk Library holds.
const TABS: string[] = [...KINDS, VOCABULARY_KIND, DOC_KIND, CHANGE_SETS_KIND, COVERAGE_TAB];

const TAB_LABEL: Record<string, string> = {
    ...KIND_LABEL,
    [VOCABULARY_KIND]: 'Your Vocabulary',
    [DOC_KIND]: 'Documents',
    [CHANGE_SETS_KIND]: 'Policy Updates',
    [COVERAGE_TAB]: 'Coverage',
};

/**
 * ONE way in for new content, on every tab: ingest a policy document.
 *
 * Entries, policies and thresholds are not authored here one at a time. What changes the
 * library is a new or revised document, and going through it is what gives each element
 * its citation, its diff against what is in force, its impact preview and a reviewer's
 * approval. A blank "New threshold" form would skip all four. Edits to what already exists
 * (customise, approve, supersede) stay on the detail pages.
 */
// Title Case, like the tabs it sits beside. "Upload" rather than "New": the user brings
// a policy they already have, not a blank document to write.
const UPLOAD_POLICY_LABEL = 'Upload Policy';

const OntologyPage: React.FC = () => {
    const navigate = useNavigate();
    const { kind } = useParams<{ kind?: string }>();
    const [searchParams] = useSearchParams();
    const setFilters = useOntologyFilters();

    // /ontology/coverage and /ontology/documents both match /ontology/:kind, and isKind
    // says no to each — so without these branches either would silently render the risk
    // library under someone else's tab.
    const isCoverage = kind === COVERAGE_TAB;
    const isDocuments = kind === DOC_KIND;
    const isChangeSets = kind === CHANGE_SETS_KIND;
    const isVocabulary = kind === VOCABULARY_KIND;
    // None has a `kind`, so none may reach apiForKind, codeOf or the preview drawer.
    const isResourceTab = !isCoverage && !isDocuments && !isChangeSets && !isVocabulary;

    // An unknown or absent :kind lands on the risk library — the vocabulary is where
    // someone with no particular question starts.
    const activeKind: OntologyKind = (isKind(kind) ? kind : 'concepts') as OntologyKind;
    const activeTab = isCoverage ? COVERAGE_TAB
        : isDocuments ? DOC_KIND
            : isChangeSets ? CHANGE_SETS_KIND
                : isVocabulary ? VOCABULARY_KIND
                    : activeKind;

    // Policy document ingestion: the upload dialog, and a counter that tells the Change
    // sets tab to re-read its lists after an upload starts or a decision lands.
    const [ingestOpen, setIngestOpen] = useState(false);
    const [changeSetsRefresh, setChangeSetsRefresh] = useState(0);

    // Filters live in the query string so a view is linkable: the agents drawer sends
    // people here with ?activity=…&status=draft already applied.
    const filters = useMemo(
        () => Object.fromEntries(searchParams.entries()),
        [searchParams],
    );

    // Which row the preview drawer is describing. In the URL rather than in state so a
    // preview is linkable and Back closes the drawer instead of leaving the page.
    const selectedCode = filters.selected ? safeDecodeCode(filters.selected) : null;

    // The clicked row, kept so the drawer can render its label, code and status on the
    // first frame instead of spinning while it fetches what the list already knew. Not in
    // the URL — it is a cache of a row, not a piece of addressable state.
    const [selectedRow, setSelectedRow] = useState<Record<string, any> | null>(null);

    const handleTabChange = (_event: React.SyntheticEvent, nextTab: string) => {
        // Filters are per-kind (source_type means nothing to a concept), so switching
        // tabs drops them rather than carrying a filter that would silently match nothing.
        setSelectedRow(null);
        navigate(`/ontology/${nextTab}`);
    };

    const handleRowSelect = useCallback((doc: Record<string, any>) => {
        // By kind. A parameter row carries a concept_code as well as its own, and a
        // fallback chain across the three would preview the wrong document.
        const code = codeOf(doc, activeKind);
        // A code containing a slash cannot be addressed as a path segment, so such a row
        // has no linkable preview and no detail page either — leave it inert rather than
        // opening a drawer whose "Open full record" would go nowhere.
        if (!isLinkableCode(code)) return;

        setSelectedRow(doc);
        setFilters({ selected: code });
    }, [activeKind, setFilters]);

    const closeDrawer = useCallback(() => {
        setSelectedRow(null);
        setFilters({ selected: '' });
    }, [setFilters]);

    const openChangeSet = useCallback((changeSetId: string) => {
        setIngestOpen(false);
        navigate(`/ontology/${CHANGE_SETS_KIND}?selected=${encodeURIComponent(changeSetId)}`);
    }, [navigate]);

    const handleIngestStarted = useCallback(() => {
        setIngestOpen(false);
        setChangeSetsRefresh((n) => n + 1);
        if (!isChangeSets) navigate(`/ontology/${CHANGE_SETS_KIND}`);
    }, [isChangeSets, navigate]);

    const browserProps = {
        filters,
        onRowSelect: handleRowSelect,
        selectedCode,
    };

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg">
            {/* Collapsed while previewing, so the drawer takes its width from the nav
                rather than from the table. Same trade the agents catalog makes. */}
            <Sidebar activePage="ontology" forceCollapsed={Boolean(selectedCode)} />

            <div className="flex flex-1 flex-col overflow-hidden">
                {/* The page's own search lives in the filter bar below and is wired to
                    the query string, so the header's presentational field is omitted
                    rather than sitting dead beside a working one. */}
                <AppHeader
                    left={(
                        <Button
                            onClick={() => navigate('/ontology')}
                            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                            sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.875rem' }}
                        >
                            Policy &amp; Risk Library
                        </Button>
                    )}
                />

                <main className="flex-1 overflow-y-auto px-8 pb-12">
                    <div className="mb-6 mt-4 flex items-center gap-4 border-b border-line">
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            sx={{
                                flex: 1,
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: dsVar('fg-2'),
                                    '&.Mui-selected': { color: dsVar('accent-fg') },
                                },
                                '& .MuiTabs-indicator': { backgroundColor: dsVar('accent') },
                            }}
                        >
                            {TABS.map((tab) => (
                                <Tab
                                    key={tab}
                                    value={tab}
                                    label={TAB_LABEL[tab] ?? tab}
                                />
                            ))}
                        </Tabs>

                        {/* The same on every tab — see UPLOAD_POLICY_LABEL. */}
                        <Button
                            onClick={() => setIngestOpen(true)}
                            startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                flexShrink: 0,
                                textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
                                backgroundColor: dsVar('accent'), color: dsVar('on-accent'), px: 1.75, py: 0.5,
                                '&:hover': { backgroundColor: '#F5D149' },
                            }}
                        >
                            {UPLOAD_POLICY_LABEL}
                        </Button>
                    </div>

                    {/* `?finding=` opens one card of the report — how the banners below
                        hand a summarised finding over to the screen that lists it in full. */}
                    {isCoverage && <CoverageBrowser focusFinding={filters.finding ?? null} />}

                    {/* The findings that are about THIS tab's records, above its table.
                        The Coverage tab keeps the whole report — including the agent-side
                        half, which has no tab of its own — and this is the same response
                        filtered, so the two can never disagree. */}
                    {!isCoverage && <CoverageBanner tab={activeTab} />}

                    {/* No onRowSelect: a document row NAVIGATES rather than opening the
                        preview drawer. The drawer previews a record you are about to read;
                        a document's whole point here is its impact panel, which needs the
                        full page. Omitting the handler is what makes OntologyBrowser
                        navigate, so there is no second code path for it. */}
                    {isDocuments && <DocumentsBrowser filters={filters} />}

                    {/* `?scenario=` opens on one rule — how Coverage and a typology's
                        "Detected by" section hand a scenario over. */}
                    {isVocabulary && <ScenarioVocabularyBrowser focusScenario={filters.scenario ?? null} />}

                    {isChangeSets && (
                        <ChangeSetsBrowser
                            filters={filters}
                            refreshKey={changeSetsRefresh}
                            onIngest={() => setIngestOpen(true)}
                        />
                    )}

                    {isResourceTab && activeKind === 'concepts' && (
                        <ConceptsBrowser {...browserProps} setFilters={setFilters} />
                    )}
                    {isResourceTab && activeKind === 'policies' && (
                        <PoliciesBrowser {...browserProps} />
                    )}
                    {isResourceTab && activeKind === 'parameters' && (
                        <ParametersBrowser {...browserProps} setFilters={setFilters} />
                    )}
                </main>
            </div>

            {/* Coverage has no rows to preview — its items link to full records — and the
                drawer fetches through apiForKind, which does not know documents. Neither
                tab mounts it; a document row navigates to its detail page instead, which
                is where the impact panel it exists for lives. */}
            <OntologyPreviewDrawer
                open={isResourceTab && Boolean(selectedCode)}
                kind={activeKind}
                code={selectedCode}
                row={selectedRow}
                onClose={closeDrawer}
            />

            {/* Docked like the preview drawer, so the queue stays in view while reviewing. */}
            <ChangeSetReviewDrawer
                changeSetId={isChangeSets ? selectedCode : null}
                onClose={closeDrawer}
                onDecided={() => setChangeSetsRefresh((n) => n + 1)}
                onIngest={() => setIngestOpen(true)}
            />

            <IngestDocumentDialog
                open={ingestOpen}
                onClose={() => setIngestOpen(false)}
                onStarted={handleIngestStarted}
                onOpenChangeSet={openChangeSet}
            />
        </div>
    );
};

export default OntologyPage;
